import {
  LocalizedEventData,
  findOccurrenceForDate,
  isEventBookableInAdvance,
} from '@carlito/bookito-client';
import { applyTimeOfDate } from '@carlito/date-utils';
import { useEffect, useMemo, useState } from 'react';

export function useBookableTimeRange(
  event: Pick<LocalizedEventData, 'timeBookability' | 'timeBookabilitySettings' | 'occurrences'>,
  onDate: Date
) {
  const canChooseTime = event.timeBookability === 'custom';

  // Update it every 5 seconds
  const [nowDate, setNowDate] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setNowDate(new Date());
    }, 1000 * 5);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const bookableTimeRange = useMemo(() => {
    if (!canChooseTime) {
      return undefined;
    }

    const occurrence = findOccurrenceForDate(onDate, event);

    if (!occurrence) {
      console.warn('Could not find occurrence for date', onDate);
      return undefined;
    }

    const startDateTime = applyTimeOfDate(onDate, new Date(occurrence.startDateTime));
    const endDateTime = new Date(startDateTime);
    endDateTime.setTime(endDateTime.getTime() + occurrence.durationMs);

    if (event.timeBookability === 'custom' && event.timeBookabilitySettings) {
      const { startTimeIncrementMs = 0, timeBlockDurationMs = 0 } = event.timeBookabilitySettings;

      const earliestPossibleBookable = startDateTime.getTime() - (startTimeIncrementMs ?? 0);
      const isPastStart = nowDate.getTime() > earliestPossibleBookable;
      const startBookingTime = isPastStart
        ? nowDate.getTime() + (startTimeIncrementMs ?? 0)
        : startDateTime.getTime();

      const safePadding = isPastStart ? 1000 * 60 * 5 : 0; // 5 minutes

      startDateTime.setTime(startBookingTime + safePadding);
      endDateTime.setTime(
        endDateTime.getTime() - (timeBlockDurationMs ?? 0) - (startTimeIncrementMs ?? 0)
      );
    }

    return {
      from: startDateTime,
      to: endDateTime,
    };
  }, [canChooseTime, onDate, event, nowDate]);

  return bookableTimeRange;
}

export function isOccurrenceAllDay(occurrence: LocalizedEventData['occurrences'][0]) {
  // const start = new Date(occurrence.startDateTime);

  return (
    // start.getHours() === 0 &&
    // start.getMinutes() === 0 &&
    occurrence.durationMs === 24 * 60 * 60 * 1000
  );
}

export function isEventAllDay(onDate: Date, event: Pick<LocalizedEventData, 'occurrences'>) {
  const occurrence = findOccurrenceForDate(onDate, event);
  if (!occurrence) {
    console.warn('No occurrence found for date', onDate, event);
    return false;
  }
  return isOccurrenceAllDay(occurrence);
}

type DerivedEventCancellationStatus =
  | {
      isCancelled: true;
      globallyCancelled: boolean;
      status: NonNullable<LocalizedEventData['cancellationStatus']>['status'];
      reason?: string;
    }
  | {
      isCancelled: false;
      status: undefined;
      reason: undefined;
      globallyCancelled: false;
    };

export function isEventCancelled(
  onDate?: Date,
  event?: Pick<LocalizedEventData, 'cancellationStatus' | 'cancelledDates'>
): DerivedEventCancellationStatus {
  if (!event) {
    return {
      isCancelled: false,
      reason: undefined,
      status: undefined,
      globallyCancelled: false,
    };
  }

  let cancellationStatus = event?.cancellationStatus?.status;
  let reason = event?.cancellationStatus?.reason;
  const isCancelledOnDate =
    onDate &&
    event.cancelledDates?.find((cancelledDate) => {
      const date = new Date(cancelledDate.date);
      const isSameDay =
        date.getDate() === onDate.getDate() &&
        date.getMonth() === onDate.getMonth() &&
        date.getFullYear() === onDate.getFullYear();

      return (
        isSameDay && (cancelledDate.status === 'cancelled' || cancelledDate.status === 'deleted')
      );
    });

  if (isCancelledOnDate) {
    cancellationStatus = isCancelledOnDate.status;
    reason = isCancelledOnDate.reason;
  }

  const isCancelled = Boolean(
    cancellationStatus === 'cancelled' || cancellationStatus === 'deleted' || isCancelledOnDate
  );

  if (isCancelled) {
    return {
      globallyCancelled: Boolean(event?.cancellationStatus),
      isCancelled: true,
      status: cancellationStatus as NonNullable<typeof cancellationStatus>,
      reason,
    };
  }

  return {
    isCancelled: false,
    status: undefined,
    reason: undefined,
    globallyCancelled: false,
  };
}

type IsEventBookable = {
  onDate: Date;
  event: Pick<
    LocalizedEventData,
    | 'bookability'
    | 'bookableUntilBeforeStartMs'
    | 'maxParticipants'
    | 'cancellationStatus'
    | 'cancelledDates'
    | 'timeBookability'
    | 'timeBookabilitySettings'
    | 'occurrences'
  >;
  numParticipants?: number;
  settings?: {
    daysBookableInAdvance?: number | null;
  };
};

export function isEventBookable({
  onDate,
  event,
  settings,
  numParticipants = 0,
}: Partial<IsEventBookable>) {
  if (!event || !onDate) {
    return {
      isBookable: false,
    };
  }

  if (
    !isEventBookableInAdvance({
      onDate,
      daysBookableInAdvance: settings?.daysBookableInAdvance,
    })
  ) {
    return {
      isBookable: false,
    };
  }

  const occurrence = findOccurrenceForDate(onDate, event);

  if (!occurrence) {
    console.warn('No occurrence found for date', onDate);
    return {
      isBookable: false,
    };
  }

  let stillBookable = true;

  const bookableUntilBeforeStartMs = event.bookableUntilBeforeStartMs;
  if (bookableUntilBeforeStartMs !== undefined && bookableUntilBeforeStartMs !== null) {
    stillBookable = onDate.getTime() - bookableUntilBeforeStartMs > Date.now();
    console.log({
      stillBookable,
      onDate,
      bookableUntilBeforeStartMs,
      aa: Date.now(),
      ss: onDate.getTime() - bookableUntilBeforeStartMs > Date.now(),
    });
  }

  let timeBookabilityPass = true;

  const eventStartDateTime = applyTimeOfDate(onDate, new Date(occurrence.startDateTime));
  const eventEndDateTime = eventStartDateTime.getTime() + occurrence.durationMs;

  if (eventEndDateTime > Date.now()) {
    stillBookable = true;
  } else {
    stillBookable = false;
  }

  console.log({
    stillBookable,
    eventEndDateTime,
    eventStartDateTime,
    aaa: occurrence.durationMs,
    aa: eventEndDateTime < Date.now(),
  });

  if (event.timeBookability === 'custom' && event.timeBookabilitySettings) {
    const { startTimeIncrementMs = 0, timeBlockDurationMs = 0 } = event.timeBookabilitySettings;
    const canBookUntil =
      eventEndDateTime - (startTimeIncrementMs ?? 0) - (timeBlockDurationMs ?? 0);
    const canStillBook = Date.now() < canBookUntil;

    timeBookabilityPass = canStillBook;
  }

  const hasBookability =
    event.bookability === 'bookable_autonomously' || event.bookability === 'bookable_on_request';

  const { isCancelled, reason } = isEventCancelled(onDate, event);

  const participantsFull = event.maxParticipants ? numParticipants > event.maxParticipants : false;
  const isBookable = !isCancelled && stillBookable && hasBookability && timeBookabilityPass;

  return {
    isBookable,
    hasBookability,
    isCancelled,
    reason,
    participantsFull,
  };
}
